/**
 * Scroll an element into view within its nearest scrollable ancestor only,
 * without affecting outer scroll containers (unlike native scrollIntoView).
 */
export function scrollIntoContainer(element: Element, options?: { behavior?: ScrollBehavior; block?: "center" | "start" | "end" | "nearest" }) {
    const { behavior = "smooth", block = "center" } = options ?? {};

    // Walk up until we find the nearest ancestor with actual overflow scrolling
    const scrollable = findScrollableAncestor(element);
    if (!scrollable) return;

    const containerRect = scrollable.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    let targetScrollTop: number;

    switch (block) {
        case "start":
            targetScrollTop = scrollable.scrollTop + (elementRect.top - containerRect.top);
            break;
        case "end":
            targetScrollTop = scrollable.scrollTop + (elementRect.bottom - containerRect.bottom);
            break;
        case "nearest": {
            const aboveViewport = elementRect.top < containerRect.top;
            const belowViewport = elementRect.bottom > containerRect.bottom;
            if (!aboveViewport && !belowViewport) return; // already visible
            targetScrollTop = aboveViewport ? scrollable.scrollTop + (elementRect.top - containerRect.top) : scrollable.scrollTop + (elementRect.bottom - containerRect.bottom);
            break;
        }
        case "center":
        default:
            targetScrollTop = scrollable.scrollTop + (elementRect.top - containerRect.top) - (containerRect.height - elementRect.height) / 2;
            break;
    }

    scrollable.scrollTo({ top: targetScrollTop, behavior });
}

function findScrollableAncestor(element: Element): Element | null {
    let current = element.parentElement;
    while (current) {
        const style = getComputedStyle(current);
        const overflowY = style.overflowY;
        if ((overflowY === "auto" || overflowY === "scroll") && current.scrollHeight > current.clientHeight) {
            return current;
        }
        current = current.parentElement;
    }
    return null;
}

function scrollEdges(el: HTMLElement): { atStart: boolean; atEnd: boolean } {
    // column-reverse scrollers (the event editor) use scrollTop 0 as the visual
    // bottom. Scrolling toward older content moves scrollTop negative.
    if (getComputedStyle(el).flexDirection === "column-reverse") {
        return {
            atEnd: el.scrollTop >= -1,
            atStart: el.scrollTop + el.scrollHeight - el.clientHeight <= 1,
        };
    }
    return {
        atStart: el.scrollTop <= 0,
        atEnd: el.scrollTop + el.clientHeight >= el.scrollHeight - 1,
    };
}

function isVerticallyScrollable(el: HTMLElement): boolean {
    const overflowY = getComputedStyle(el).overflowY;
    return (overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight + 1;
}

function nearestScrollableWithin(node: Node | null, boundary: HTMLElement): HTMLElement | null {
    let current: HTMLElement | null = node instanceof HTMLElement ? node : node?.parentElement ?? null;
    while (current) {
        if (isVerticallyScrollable(current)) return current;
        if (current === boundary) break;
        current = current.parentElement;
    }
    return isVerticallyScrollable(boundary) ? boundary : null;
}

function wheelDeltaY(e: WheelEvent): number {
    if (e.deltaMode === 1) return e.deltaY * 16;
    if (e.deltaMode === 2) return e.deltaY * (e.currentTarget instanceof HTMLElement ? e.currentTarget.clientHeight : 800);
    return e.deltaY;
}

/**
 * Keep wheel / trackpad scrolling inside `el`. At the top or bottom of the
 * nearest inner scroller, preventDefault so ancestor columns (the chat
 * transcript) cannot be grabbed by overscroll.
 */
export function containNestedScroll(el: HTMLElement): () => void {
    const onWheel = (e: WheelEvent) => {
        const dy = wheelDeltaY(e);
        if (dy === 0) return;
        const scrollable = nearestScrollableWithin(e.target as Node, el);
        if (!scrollable) {
            e.preventDefault();
            return;
        }
        const { atStart, atEnd } = scrollEdges(scrollable);
        if ((dy < 0 && atStart) || (dy > 0 && atEnd)) {
            e.preventDefault();
        }
    };
    el.addEventListener("wheel", onWheel, { passive: false, capture: true });
    return () => el.removeEventListener("wheel", onWheel, { capture: true });
}
